import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Toast, ToastContainer } from 'react-bootstrap';
import { get, getProfile } from '../utils/api';
import { FaBoxOpen, FaShoppingCart, FaUsers, FaTruck, FaExclamationTriangle, FaCartPlus, FaChartPie, FaChartLine, FaFire, FaCalendarAlt, FaDollarSign, FaArrowUp, FaArrowDown, FaDownload } from 'react-icons/fa';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import './Dashboard.css';
import RecentOrders from './RecentOrders'; // Recent orders panel
import config, { CURRENCY_SYMBOL } from '../config';
import { useTheme } from '../utils/ThemeContext';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);


const translations = {
  en: {
    welcome: 'Welcome Back',
    overview: "Here's a quick overview of your store's performance.",
    totalUsers: 'Total Users',
    totalMedicines: 'Total Medicines',
    lowStock: 'Low Stock',
    totalOrders: 'Total Orders',
    totalSuppliers: 'Total Suppliers',
    totalCustomers: 'Total Customers',
    totalDeliveryPersons: 'Total Delivery Persons',
    salesChart: 'Sales Chart',
    calendar: 'Calendar',
    monthlySales: 'Monthly Sales',
    salesOverTime: 'Sales Over Time',
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
    ],
    recentOrders: 'Recent Orders',
  },

  bn: {
    welcome: 'আবার স্বাগতম',
    overview: 'আপনার স্টোরের কার্যক্ষমতার একটি সংক্ষিপ্ত বিবরণ এখানে দেওয়া হলো।',
    totalUsers: 'মোট ব্যবহারকারী',
    totalMedicines: 'মোট ওষুধ',
    lowStock: 'কম স্টক',
    totalOrders: 'মোট অর্ডার',
    totalSuppliers: 'মোট সরবরাহকারী',
    totalCustomers: 'মোট গ্রাহক',
    totalDeliveryPersons: 'মোট ডেলিভারি পার্সন',
    salesChart: 'বিক্রির চার্ট',
    calendar: 'ক্যালেন্ডার',
    monthlySales: 'মাসিক বিক্রি',
    salesOverTime: 'সময়ের সাথে বিক্রি',
    months: [
      'জানুয়ারি',
      'ফেব্রুয়ারি',
      'মার্চ',
      'এপ্রিল',
      'মে',
      'জুন',
      'জুলাই',
    ],
    recentOrders: 'সাম্প্রতিক অর্ডার',
  },

  hi: {
    welcome: 'आपका फिर से स्वागत है',
    overview: 'यह आपके स्टोर के प्रदर्शन का एक त्वरित अवलोकन है।',
    totalUsers: 'कुल उपयोगकर्ता',
    totalMedicines: 'कुल दवाएं',
    lowStock: 'कम स्टॉक',
    totalOrders: 'कुल आदेश',
    totalSuppliers: 'कुल आपूर्तिकर्ता',
    totalCustomers: 'कुल ग्राहक',
    totalDeliveryPersons: 'कुल डिलीवरी व्यक्ति',
    salesChart: 'बिक्री चार्ट',
    calendar: 'कैलेंडर',
    monthlySales: 'मासिक बिक्री',
    salesOverTime: 'समय के साथ बिक्री',
    months: [
      'जनवरी',
      'फ़रवरी',
      'मार्च',
      'अप्रैल',
      'मई',
      'जून',
      'जुलाई',
    ],
    recentOrders: 'हाल के ऑर्डर',
  },
};

const Dashboard = ({ language = 'en' }) => {
  const t = translations[language];
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [comparisonMode, setComparisonMode] = useState(false);
  const [compareYear, setCompareYear] = useState(new Date().getFullYear() - 1);
  const [summaryData, setSummaryData] = useState({
    todays_revenue: { value: 0, change_percent: 0 },
    todays_orders: { value: 0, change_percent: 0 },
    todays_new_customers: { value: 0, change_percent: 0 },
    pending_deliveries: 0
  });
  const [salesAnalytics, setSalesAnalytics] = useState({
    selectedYear: Array(12).fill(0),
    compareYear: Array(12).fill(0)
  });
  const [userGrowth, setUserGrowth] = useState({
    monthly: Array(12).fill(0),
    totalThisYear: 0,
    growthRate: 0
  });
  const [orderDistribution, setOrderDistribution] = useState({
    pending: 0,
    confirmed: 0,
    dispatched: 0,
    delivered: 0,
    cancelled: 0
  });
  const [widgets, setWidgets] = useState({
    lowStock: [],
    expiring: [],
    topSelling: [],
    revenueTrends: {
      today: { value: 0, change: 0 },
      week: { value: 0, change: 0 },
      month: { value: 0, change: 0 },
      year: { value: 0, change: 0 }
    },
    dailyRevenue: {
      labels: [],
      values: Array(7).fill(0)
    },
    miniStats: {
      totalSales: 0,
      totalCustomers: 0,
      totalMedicines: 0
    }
  });

  // Fetch sales analytics
  useEffect(() => {
    const fetchSales = async () => {
      try {
        let url = `/reports/dashboard/sales-analytics?selectedYear=${selectedYear}`;
        if (comparisonMode && compareYear) {
          url += `&compareYear=${compareYear}`;
        }
        const res = await get(url);
        if (res && res.success) {
          setSalesAnalytics({
            selectedYear: res.data.selectedYear || Array(12).fill(0),
            compareYear: res.data.compareYear || Array(12).fill(0)
          });
        }
      } catch (error) {
        console.error('Failed to fetch sales analytics:', error);
      }
    };
    fetchSales();
  }, [selectedYear, compareYear, comparisonMode]);

  // Fetch user growth
  useEffect(() => {
    const fetchGrowth = async () => {
      try {
        const res = await get(`/reports/dashboard/user-growth?year=${selectedYear}`);
        if (res && res.success) {
          setUserGrowth({
            monthly: res.data.monthly || Array(12).fill(0),
            totalThisYear: res.data.totalThisYear || 0,
            growthRate: res.data.growthRate || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch user growth:', error);
      }
    };
    fetchGrowth();
  }, [selectedYear]);

  // Fetch initial profile, summary, widgets & order distribution
  useEffect(() => {
    const welcomeToastShown = sessionStorage.getItem('welcomeToastShown');
    if (!welcomeToastShown) {
      setShowWelcomeToast(true);
      sessionStorage.setItem('welcomeToastShown', 'true');
    }

    const fetchData = async () => {
      try {
        const profileRes = await getProfile();
        if (profileRes && profileRes.success) {
          setCurrentUser(profileRes.data);
        }

        // Fetch dashboard summary
        const summaryRes = await get('/reports/dashboard/summary');
        if (summaryRes && summaryRes.success) {
          const data = summaryRes.data;
          setSummaryData({
            todays_revenue: {
              value: data.todays_revenue?.value || 0,
              change_percent: data.todays_revenue?.change_percent ?? 0
            },
            todays_orders: {
              value: data.todays_orders?.value || 0,
              change_percent: data.todays_orders?.change_percent ?? 0
            },
            todays_new_customers: {
              value: data.todays_new_customers?.value || 0,
              change_percent: data.todays_new_customers?.change_percent ?? 0
            },
            pending_deliveries: data.pending_deliveries || 0
          });
        }

        // Fetch order distribution
        const distRes = await get('/reports/dashboard/order-distribution');
        if (distRes && distRes.success) {
          setOrderDistribution(distRes.data);
        }

        // Fetch widgets with fallback for Low Stock
        let lowStockList = [];
        let expiringList = [];
        let topSellingList = [];
        let revenueTrendsData = {
          today: { value: 0, change: 0 },
          week: { value: 0, change: 0 },
          month: { value: 0, change: 0 },
          year: { value: 0, change: 0 }
        };
        let dailyRevenueData = { labels: [], values: Array(7).fill(0) };
        let miniStatsData = { totalSales: 0, totalCustomers: 0, totalMedicines: 0 };

        try {
          const widgetsRes = await get('/reports/dashboard/widgets');
          if (widgetsRes && widgetsRes.success && widgetsRes.data) {
            const w = widgetsRes.data;
            lowStockList = w.lowStock || w.low_stock || w.lowStockItems || [];
            expiringList = w.expiring || w.expiringSoon || [];
            topSellingList = w.topSelling || w.top_selling || [];
            if (w.revenueTrends) revenueTrendsData = w.revenueTrends;
            if (w.dailyRevenue) dailyRevenueData = w.dailyRevenue;
            if (w.miniStats) miniStatsData = w.miniStats;
          }
        } catch (err) {
          console.error('Failed to fetch dashboard widgets:', err);
        }

        // Fallback for Low Stock Items if widget array is empty
        if (!lowStockList || lowStockList.length === 0) {
          try {
            const lowStockRes = await get('/purchase-orders/low-stock-suggestions');
            if (lowStockRes && lowStockRes.success && Array.isArray(lowStockRes.data)) {
              lowStockList = lowStockRes.data;
            }
          } catch (err) {
            console.error('Failed to fetch fallback low stock items:', err);
          }
        }

        setWidgets({
          lowStock: lowStockList,
          expiring: expiringList,
          topSelling: topSellingList,
          revenueTrends: revenueTrendsData,
          dailyRevenue: dailyRevenueData,
          miniStats: miniStatsData
        });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [t]);

  // sales chart removed per user request

  // Bar chart data for Analytics Dashboard
  const barChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: comparisonMode ? [
      {
        label: `${selectedYear}`,
        data: salesAnalytics.selectedYear,
        backgroundColor: 'rgba(22, 170, 245, 0.9)',
        borderRadius: 6,
        barThickness: 14,
      },
      {
        label: `${compareYear}`,
        data: salesAnalytics.compareYear,
        backgroundColor: 'rgba(205, 244, 9, 0.87)',
        borderRadius: 6,
        barThickness: 14,
      },
    ] : [
      {
        label: 'Sales',
        data: salesAnalytics.selectedYear,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(252, 112, 43, 0.9)',
        borderRadius: 8,
        barThickness: 16,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: comparisonMode,
        position: 'top',
        align: 'end',
        labels: {
          color: isDark ? 'rgba(255,255,255,0.9)' : 'var(--text-color)',
          font: { size: 11, weight: '700' },
          boxWidth: 12,
          boxHeight: 12,
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: isDark ? 'rgba(255,255,255,0.75)' : 'var(--text-muted-color)', font: { size: 11, weight: '600' } }
      },
      y: {
        grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-color)', lineWidth: 1 },
        ticks: { color: isDark ? 'rgba(255,255,255,0.75)' : 'var(--text-muted-color)', font: { size: 11, weight: '600' } }
      },
    },
  };

  const handleDownloadReport = async () => {
    try {
      // Query up to 10,000 orders to bypass default 20 order pagination limit
      const res = await get('/orders?limit=10000');
      if (res && res.success) {
        const allOrders = Array.isArray(res.data) ? res.data : [];
        
        // Filter orders by selectedYear or compareYear
        const filteredOrders = allOrders.filter(o => {
          const orderYear = new Date(o.order_date).getFullYear();
          return orderYear === selectedYear || (comparisonMode && orderYear === compareYear);
        });

        // Generate CSV content
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Order ID,Customer Name,Customer Mobile,Order Type,Date & Time,Medicine Name,Quantity,Unit Price,Item Subtotal,Order Total Amount,Payment Method,Payment Status,Transaction ID,Gateway Name,Fulfillment Status,Delivery Address\n";

        filteredOrders.forEach(o => {
          const dateStr = new Date(o.order_date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(/,/g, ''); // Remove commas to prevent breaking cells
          
          const customer = (o.customer_name || 'Guest').replace(/,/g, '');
          const mobile = o.customer_mobile || 'N/A';
          const type = o.order_type ? o.order_type.toUpperCase() : 'OFFLINE';
          const amount = o.total_amount;
          const paymentMethod = o.payment_method ? o.payment_method.toUpperCase() : 'CASH';
          const paymentStatus = o.payment_status ? o.payment_status.toUpperCase() : 'PENDING';
          const transactionId = o.transaction_id || 'N/A';
          const gateway = o.gateway_name || 'N/A';
          const fulfillmentStatus = o.status ? o.status.toUpperCase() : 'PENDING';
          
          const addressParts = [
            o.delivery_address?.address_line1,
            o.delivery_address?.address_line2,
            o.delivery_address?.city,
            o.delivery_address?.state,
            o.delivery_address?.postal_code,
            o.delivery_address?.country
          ].filter(Boolean).map(a => a.replace(/,/g, '').trim());
          const address = addressParts.length > 0 ? addressParts.join(' ') : 'Store POS Pickup';

          const items = Array.isArray(o.items) ? o.items : [];
          if (items.length === 0) {
            csvContent += `"${o.order_id}","${customer}","${mobile}","${type}","${dateStr}","N/A",0,0,0,${amount},"${paymentMethod}","${paymentStatus}","${transactionId}","${gateway}","${fulfillmentStatus}","${address}"\n`;
          } else {
            items.forEach(item => {
              const medName = (item.medicine_name || 'Unknown').replace(/,/g, '');
              const qty = item.quantity || 0;
              const price = item.unit_price || 0;
              const subtotal = item.subtotal || 0;
              
              csvContent += `"${o.order_id}","${customer}","${mobile}","${type}","${dateStr}","${medName}",${qty},${price},${subtotal},${amount},"${paymentMethod}","${paymentStatus}","${transactionId}","${gateway}","${fulfillmentStatus}","${address}"\n`;
            });
          }
        });

        // Download
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `sales_report_details_${selectedYear}${comparisonMode ? `_vs_${compareYear}` : ''}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Failed to download detailed orders report", err);
    }
  };

  // Line chart data for User Growth
  const userGrowthData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'New Users',
        data: userGrowth.monthly,
        borderColor: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(249, 115, 22, 0.9)',
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(249, 115, 22, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: isDark ? 'rgba(255, 255, 255, 1)' : 'rgba(249, 115, 22, 1)',
        pointBorderColor: isDark ? 'rgba(249, 115, 22, 0.8)' : '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 7,
      },
    ],
  };

  const userGrowthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 13, weight: '700' },
        bodyFont: { size: 12 },
        borderColor: 'rgba(249,115,22,0.6)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: isDark ? 'rgba(255,255,255,0.75)' : 'var(--text-muted-color)', font: { size: 11, weight: '600' } },
      },
      y: {
        grid: { color: isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-color)', lineWidth: 1 },
        ticks: { color: isDark ? 'rgba(255,255,255,0.75)' : 'var(--text-muted-color)', font: { size: 11, weight: '600' } },
        beginAtZero: true,
      },
    },
  };

  // Doughnut chart data for Order Status Distribution
  const orderStatusData = {
    labels: ['Delivered', 'Pending', 'Cancelled', 'Processing'],
    datasets: [
      {
        data: [
          orderDistribution.delivered || 0,
          orderDistribution.pending || 0,
          orderDistribution.cancelled || 0,
          (orderDistribution.confirmed || 0) + (orderDistribution.dispatched || 0)
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.85)',
          'rgba(245, 158, 11, 0.85)',
          'rgba(239, 68, 68, 0.85)',
          'rgba(59, 130, 246, 0.85)',
        ],
        borderColor: 'var(--card-background-color)',
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const orderStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          color: isDark ? 'rgba(255,255,255,0.9)' : 'var(--text-color)',
          font: { size: 12, weight: '600' },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)',
        padding: 12,
        titleFont: { size: 13, weight: '700' },
        bodyFont: { size: 12, weight: '600' },
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const value = context.parsed;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      },
    },
  };

  const totalOrdersCount = (orderDistribution.delivered || 0) +
    (orderDistribution.pending || 0) +
    (orderDistribution.cancelled || 0) +
    (orderDistribution.confirmed || 0) +
    (orderDistribution.dispatched || 0);

  const getPercentage = (val) => {
    if (totalOrdersCount === 0) return '0.0';
    return ((val / totalOrdersCount) * 100).toFixed(1);
  };

  const maxDailyRevenue = Math.max(...widgets.dailyRevenue.values, 1000);

  return (
    <Container fluid className="dashboard-root px-4 px-sm-5">
      <ToastContainer position="center-center" className="p-3">
        <Toast
          onClose={() => setShowWelcomeToast(false)}
          show={showWelcomeToast}
          delay={5000}
          autohide
          bg="success"
          className="welcome-toast"
        >
          <Toast.Header>
            <strong className="me-auto">{config.appName}</strong>
            <small>just now</small>
          </Toast.Header>
          <Toast.Body>{t.welcome} {currentUser ? currentUser.name : 'Boss'}</Toast.Body> {/* Display user's name */}
        </Toast>
      </ToastContainer>

      {/* Quick Access - useful shortcuts */}
      <Row className="quick-access-row mb-4">
        <Col xs={12}>
          <div className="quick-access">
            {[
              { key: 'medicines', label: 'Medicine', icon: <FaBoxOpen />, href: '/medicines', bg: 'linear-gradient(135deg, #6366f1, #a855f7)' },
              { key: 'newOrder', label: 'New Order', icon: <FaCartPlus />, href: '/offline-sell', bg: 'linear-gradient(135deg, #10b981, #06b6d4)' },
              { key: 'suppliers', label: 'Suppliers', icon: <FaTruck />, href: '/suppliers', bg: 'linear-gradient(135deg, #f59e0b, #ef4444)' },
              { key: 'customers', label: 'Customers', icon: <FaUsers />, href: '/users?role=customer', bg: 'linear-gradient(135deg, #ec4899, #8b5cf6)' },
              { key: 'inventory', label: 'Inventory', icon: <FaBoxOpen />, href: '/batches', bg: 'linear-gradient(135deg, #14b8a6, #22d3ee)' },
              { key: 'orders', label: 'Orders', icon: <FaShoppingCart />, href: '/orders', bg: 'linear-gradient(135deg, #f43f5e, #fb923c)' },
            ].map((it) => (
              <button
                key={it.key}
                className="quick-card"
                onClick={() => { window.location.href = it.href }}
                aria-label={it.label}
              >
                <div className="quick-card-icon" aria-hidden style={{ background: it.bg }}>
                  {it.icon}
                </div>
                <div className="quick-card-label">{it.label}</div>
              </button>
            ))}
          </div>
        </Col>
      </Row>

      {/* Today's Overview Stats */}
      <Row className="mb-4">
        <Col lg={3} sm={6}>
          <Card className="today-stat-card">
            <Card.Body>
              <div className="today-stat-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                <FaDollarSign />
              </div>
              <div className="today-stat-content">
                <div className="today-stat-label">Today's Revenue</div>
                <div className="today-stat-value">{CURRENCY_SYMBOL}{summaryData.todays_revenue.value.toLocaleString()}</div>
                <div className={`today-stat-change ${summaryData.todays_revenue.change_percent >= 0 ? 'positive' : 'negative'}`}>
                  {summaryData.todays_revenue.change_percent >= 0 ? '+' : ''}{summaryData.todays_revenue.change_percent}% from yesterday
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="today-stat-card">
            <Card.Body>
              <div className="today-stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #06b6d4)' }}>
                <FaShoppingCart />
              </div>
              <div className="today-stat-content">
                <div className="today-stat-label">Today's Orders</div>
                <div className="today-stat-value">{summaryData.todays_orders.value}</div>
                <div className={`today-stat-change ${summaryData.todays_orders.change_percent >= 0 ? 'positive' : 'negative'}`}>
                  {summaryData.todays_orders.change_percent >= 0 ? '+' : ''}{summaryData.todays_orders.change_percent}% from yesterday
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="today-stat-card">
            <Card.Body>
              <div className="today-stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}>
                <FaUsers />
              </div>
              <div className="today-stat-content">
                <div className="today-stat-label">New Customers</div>
                <div className="today-stat-value">{summaryData.todays_new_customers.value}</div>
                <div className={`today-stat-change ${summaryData.todays_new_customers.change_percent >= 0 ? 'positive' : 'negative'}`}>
                  {summaryData.todays_new_customers.change_percent >= 0 ? '+' : ''}{summaryData.todays_new_customers.change_percent}% from yesterday
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="today-stat-card">
            <Card.Body>
              <div className="today-stat-icon" style={{ background: 'linear-gradient(135deg, #ec4899, #8b5cf6)' }}>
                <FaTruck />
              </div>
              <div className="today-stat-content">
                <div className="today-stat-label">Pending Deliveries</div>
                <div className="today-stat-value">{summaryData.pending_deliveries}</div>
                <div className="today-stat-change">Ready to ship</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={8} className="mb-4">
          <Card className="analytics-dashboard-card shadow-lg h-100">
            <Card.Body>
              <div className="analytics-header">
                <div>
                  <div className="analytics-title">
                    <FaChartPie className="me-2" />
                    Sales Analytics
                  </div>
                  <div className="analytics-sub">Real-time sales, inventory & order insights</div>
                </div>
                <div className="analytics-controls">
                  <div className="comparison-toggle">
                    <input
                      type="checkbox"
                      id="compareToggle"
                      checked={comparisonMode}
                      onChange={(e) => setComparisonMode(e.target.checked)}
                    />
                    <label htmlFor="compareToggle">Compare Years</label>
                  </div>
                  <div className="year-selector">
                    <button
                      className="year-nav-btn"
                      onClick={() => setSelectedYear(selectedYear - 1)}
                      aria-label="Previous year"
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <span className="year-display">{selectedYear}</span>
                    <button
                      className="year-nav-btn"
                      onClick={() => setSelectedYear(selectedYear + 1)}
                      aria-label="Next year"
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  {comparisonMode && (
                    <div className="year-selector compare">
                      <button
                        className="year-nav-btn"
                        onClick={() => setCompareYear(compareYear - 1)}
                        aria-label="Previous compare year"
                      >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <span className="year-display">{compareYear}</span>
                      <button
                        className="year-nav-btn"
                        onClick={() => setCompareYear(compareYear + 1)}
                        aria-label="Next compare year"
                      >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <button
                    className="year-nav-btn me-2"
                    onClick={handleDownloadReport}
                    title="Download CSV Report"
                    style={{ minWidth: '38px', height: '38px', borderRadius: '12px' }}
                  >
                    <FaDownload />
                  </button>
                  <button className="analytics-btn" onClick={() => { window.location.href = '/orders' }}>
                    <span>Full Report</span>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mini stat cards */}
              <div className="analytics-stats">
                <div className="stat-mini">
                  <div className="stat-mini-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                    <FaShoppingCart />
                  </div>
                  <div>
                    <div className="stat-mini-value">{widgets.miniStats.totalSales.toLocaleString()}</div>
                    <div className="stat-mini-label">Total Sales</div>
                  </div>
                </div>
                <div className="stat-mini">
                  <div className="stat-mini-icon" style={{ background: 'linear-gradient(135deg, #10b981, #06b6d4)' }}>
                    <FaUsers />
                  </div>
                  <div>
                    <div className="stat-mini-value">{widgets.miniStats.totalCustomers.toLocaleString()}</div>
                    <div className="stat-mini-label">Customers</div>
                  </div>
                </div>
                <div className="stat-mini">
                  <div className="stat-mini-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}>
                    <FaBoxOpen />
                  </div>
                  <div>
                    <div className="stat-mini-value">{widgets.miniStats.totalMedicines.toLocaleString()}</div>
                    <div className="stat-mini-label">Products</div>
                  </div>
                </div>
              </div>

              <div className="analytics-chart-container">
                <Bar data={barChartData} options={barChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card className="recent-orders-card shadow-sm h-100">
            <Card.Body>
              <div className="recent-orders-header">
                <div>
                  <div className="recent-orders-title">{t.recentOrders}</div>
                  <div className="recent-orders-sub">Latest customer orders & statuses</div>
                </div>
                <button className="view-report-btn small" onClick={() => { window.location.href = '/orders' }}>View All</button>
              </div>

              <div className="recent-orders-list">
                <RecentOrders />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Alert Widgets Row */}
      <Row className="mb-4">
        <Col lg={4}>
          <Card className="alert-widget-card low-stock-card">
            <Card.Body>
              <div className="alert-widget-header">
                <div className="alert-widget-title">
                  <FaExclamationTriangle className="me-2" />
                  Low Stock Alert
                </div>
                <span className="alert-badge">{widgets.lowStock.length} Items</span>
              </div>
              <div className="alert-widget-list">
                {widgets.lowStock.length === 0 ? (
                  <div className="alert-item py-3 text-center text-white-50">No low stock items</div>
                ) : (
                  widgets.lowStock.slice(0, 5).map((item, idx) => {
                    const medName = item.medicine_name || item.name || item.title || 'Medicine';
                    const stockQty = item.current_stock ?? item.stock_quantity ?? item.stock ?? item.total_stock ?? 0;
                    const unitStr = item.unit_name ? ` ${item.unit_name}s` : ' units';
                    return (
                      <div key={idx} className="alert-item">
                        <div className="alert-item-info">
                          <div className="alert-item-name">{medName}</div>
                          <div className="alert-item-detail">Only {stockQty}{unitStr} left</div>
                        </div>
                        <button className="alert-action-btn" onClick={() => window.location.href = '/purchases?tab=lowstock'}>Reorder</button>
                      </div>
                    );
                  })
                )}
              </div>
              <button className="alert-view-all" onClick={() => { window.location.href = '/purchases?tab=lowstock' }}>
                View All Low Stock Items
              </button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="alert-widget-card expiring-card">
            <Card.Body>
              <div className="alert-widget-header">
                <div className="alert-widget-title">
                  <FaCalendarAlt className="me-2" />
                  Expiring Soon
                </div>
                <span className="alert-badge warning">{widgets.expiring.length} Items</span>
              </div>
              <div className="alert-widget-list">
                {widgets.expiring.length === 0 ? (
                  <div className="alert-item py-3 text-center text-white-50">No expiring items</div>
                ) : (
                  widgets.expiring.slice(0, 3).map((item, idx) => {
                    const daysLeft = Math.ceil((new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={idx} className="alert-item">
                        <div className="alert-item-info">
                          <div className="alert-item-name">{item.medicine_name}</div>
                          <div className="alert-item-detail">Expires in {daysLeft} days</div>
                        </div>
                        <button className="alert-action-btn warning" onClick={() => window.location.href = '/promotions'}>Discount</button>
                      </div>
                    );
                  })
                )}
              </div>
              <button className="alert-view-all" onClick={() => { window.location.href = '/medicines?filter=expiring' }}>
                View All Expiring Items
              </button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="alert-widget-card top-selling-card">
            <Card.Body>
              <div className="alert-widget-header">
                <div className="alert-widget-title">
                  <FaFire className="me-2" />
                  Top Selling
                </div>
                <span className="alert-badge success">This Month</span>
              </div>
              <div className="alert-widget-list">
                {widgets.topSelling.length === 0 ? (
                  <div className="alert-item py-3 text-center text-white-50">No data available</div>
                ) : (
                  widgets.topSelling.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="alert-item">
                      <div className="top-rank">{idx + 1}</div>
                      <div className="alert-item-info">
                        <div className="alert-item-name">{item.medicine_name}</div>
                        <div className="alert-item-detail">{item.total_sold} units sold</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button className="alert-view-all" onClick={() => { window.location.href = '/medicines' }}>
                View Full Report
              </button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Revenue Trends & Order Status */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="revenue-trends-card shadow-lg mb-4">
            <Card.Body>
              <div className="revenue-trends-header">
                <div className="revenue-trends-title">
                  <FaDollarSign className="me-2" />
                  Revenue Trends
                </div>
                <div className="revenue-trends-sub">Compare revenue across time periods</div>
              </div>
              <div className="revenue-comparison-grid">
                <div className="revenue-period">
                  <div className="period-label">Today</div>
                  <div className="period-value">{CURRENCY_SYMBOL}{widgets.revenueTrends.today.value.toLocaleString()}</div>
                  <div className={`period-change ${widgets.revenueTrends.today.change >= 0 ? 'positive' : 'negative'}`}>
                    {widgets.revenueTrends.today.change >= 0 ? <FaArrowUp /> : <FaArrowDown />} {widgets.revenueTrends.today.change >= 0 ? '+' : ''}{widgets.revenueTrends.today.change}%
                  </div>
                </div>
                <div className="revenue-period">
                  <div className="period-label">This Week</div>
                  <div className="period-value">{CURRENCY_SYMBOL}{widgets.revenueTrends.week.value.toLocaleString()}</div>
                  <div className={`period-change ${widgets.revenueTrends.week.change >= 0 ? 'positive' : 'negative'}`}>
                    {widgets.revenueTrends.week.change >= 0 ? <FaArrowUp /> : <FaArrowDown />} {widgets.revenueTrends.week.change >= 0 ? '+' : ''}{widgets.revenueTrends.week.change}%
                  </div>
                </div>
                <div className="revenue-period">
                  <div className="period-label">This Month</div>
                  <div className="period-value">{CURRENCY_SYMBOL}{widgets.revenueTrends.month.value.toLocaleString()}</div>
                  <div className={`period-change ${widgets.revenueTrends.month.change >= 0 ? 'positive' : 'negative'}`}>
                    {widgets.revenueTrends.month.change >= 0 ? <FaArrowUp /> : <FaArrowDown />} {widgets.revenueTrends.month.change >= 0 ? '+' : ''}{widgets.revenueTrends.month.change}%
                  </div>
                </div>
                <div className="revenue-period">
                  <div className="period-label">This Year</div>
                  <div className="period-value">{CURRENCY_SYMBOL}{widgets.revenueTrends.year.value.toLocaleString()}</div>
                  <div className={`period-change ${widgets.revenueTrends.year.change >= 0 ? 'positive' : 'negative'}`}>
                    {widgets.revenueTrends.year.change >= 0 ? <FaArrowUp /> : <FaArrowDown />} {widgets.revenueTrends.year.change >= 0 ? '+' : ''}{widgets.revenueTrends.year.change}%
                  </div>
                </div>
              </div>
              <div className="revenue-mini-chart">
                <div className="mini-chart-label">Last 7 Days Revenue</div>
                <div className="mini-bars">
                  {widgets.dailyRevenue.values.map((val, idx) => {
                    const label = widgets.dailyRevenue.labels[idx] || '';
                    const displayDate = label ? new Date(label).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
                    return (
                      <div key={idx} className="mini-bar" style={{ height: `${(val / maxDailyRevenue) * 100}%` }}>
                        <div className="mini-bar-tooltip">{displayDate}: {CURRENCY_SYMBOL}{(val / 1000).toFixed(1)}K</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="order-status-card shadow-lg mb-4">
            <Card.Body>
              <div className="order-status-header">
                <div className="order-status-title">
                  <FaChartPie className="me-2" />
                  Order Status Distribution
                </div>
                <div className="order-status-sub">Breakdown of all orders by status</div>
              </div>
              <div className="order-status-chart-container">
                <Doughnut data={orderStatusData} options={orderStatusOptions} />
              </div>
              <div className="order-status-summary">
                <div className="status-summary-item">
                  <div className="status-dot delivered"></div>
                  <span className="status-summary-label">Delivered:</span>
                  <span className="status-summary-value">
                    {orderDistribution.delivered || 0} ({getPercentage(orderDistribution.delivered || 0)}%)
                  </span>
                </div>
                <div className="status-summary-item">
                  <div className="status-dot pending"></div>
                  <span className="status-summary-label">Pending:</span>
                  <span className="status-summary-value">
                    {orderDistribution.pending || 0} ({getPercentage(orderDistribution.pending || 0)}%)
                  </span>
                </div>
                <div className="status-summary-item">
                  <div className="status-dot cancelled"></div>
                  <span className="status-summary-label">Cancelled:</span>
                  <span className="status-summary-value">
                    {orderDistribution.cancelled || 0} ({getPercentage(orderDistribution.cancelled || 0)}%)
                  </span>
                </div>
                <div className="status-summary-item">
                  <div className="status-dot processing"></div>
                  <span className="status-summary-label">Processing:</span>
                  <span className="status-summary-value">
                    {(orderDistribution.confirmed || 0) + (orderDistribution.dispatched || 0)} ({getPercentage((orderDistribution.confirmed || 0) + (orderDistribution.dispatched || 0))}%)
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Growth Analytics */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="user-growth-card shadow-lg mb-4">
            <Card.Body>
              <div className="user-growth-header">
                <div>
                  <div className="user-growth-title">
                    <FaChartLine className="me-2" />
                    User Growth
                  </div>
                  <div className="user-growth-sub">Track new user registrations & growth trends</div>
                </div>
                <div className="user-growth-stats-inline">
                  <div className="stat-inline">
                    <div className="stat-inline-value">+{userGrowth.totalThisYear}</div>
                    <div className="stat-inline-label">This Year</div>
                  </div>
                  <div className="stat-inline">
                    <div className="stat-inline-value">+{userGrowth.growthRate}%</div>
                    <div className="stat-inline-label">Growth Rate</div>
                  </div>
                </div>
              </div>
              <div className="user-growth-chart-container">
                <Line data={userGrowthData} options={userGrowthOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;